import {
    AfterViewInit,
    Component,
    effect,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    input,
    Input,
    OnInit,
    Output,
    signal,
    ViewChild,
    WritableSignal
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { SharedLanguageService } from '@language';
import { BehaviorSubject } from 'rxjs';
import { UrlQueryParams } from '../../../core/query/entities/url-query-params.entity';
import { GenerateQueryParamsFromAdvancedSearchTerm } from '../../../core/query/helpers/query-params-generators.helper';
import { PERSON_ID_DEFINITION_DOTTED_KEY } from '../../../facial-recognition/constants/field-definition-keys.constants';
import { Face } from '../../../facial-recognition/entities/face.entity';
import { FaceInteractionService } from '../../../facial-recognition/services/face-interaction.service';
import { ShSearchRoutingService } from '../../../profile-form/services/search-routing.service';
import { ActionItem } from '../action-item/entities/action-item.entity';
import { ShIconModule } from '../icon/icon.module';
import { ImageViewerTranslations, ImageViewerTranslationsPartial } from './entities/image-viewer-translations.entity';

export * from './entities/image-viewer-translations.entity';

export enum ViewerActionsPosition {
    TOP_OVERLAY = '-sh-top',
    RIGHT_OVERLAY = '-sh-right',
    BOTTOM_OVERLAY = '-sh-bottom',
    LEFT_OVERLAY = '-sh-left',
    BOTTOM_EXTERNAL = '-sh-viewer-button'
}

interface FaceFrame {
    id: string;
    name?: string;
    personId?: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 *  Forked from https://github.com/deepakgonda/angular-image-viewer project
 *  Modified for usage in combination with Stylehaven
 */
@Component({
    standalone: true,
    selector: 'sh-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: ['./image-viewer.component.scss'],
    imports: [CommonModule, ShIconModule, RouterLink]
})
export class ShImageViewerComponent implements OnInit, AfterViewInit {
    public readonly ViewerActionsPosition: typeof ViewerActionsPosition = ViewerActionsPosition;
    private readonly ctrlPressed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public style: any = { transform: '', msTransform: '', oTransform: '', webkitTransform: '' };
    public loading = true;
    public dragging = false;
    public draggable = true;
    public transition = false;
    public imageActionButtons!: ActionItem[];
    public _imageSources: string[] = [];

    protected scale = 1;
    protected rotation = 0;
    protected translateX = 0;
    protected translateY = 0;
    private prevX!: number;
    private prevY!: number;
    private hovered = false;
    private imageResizeObserver: ResizeObserver;
    public showFaceFrames = false;
    public $focusedFaceId: WritableSignal<string | null>;
    public $hoveredFaceId: WritableSignal<string | null>;

    private readonly defaultTranslations: ImageViewerTranslations = {
        zoomIn: 'image-viewer.zoomIn',
        zoomOut: 'image-viewer.zoomOut',
        rotateClockwise: 'image-viewer.rotateClockwise',
        rotateCounterClockwise: 'image-viewer.rotateCounterClockwise',
        undo: 'image-viewer.undo',
        next: 'image-viewer.next',
        previous: 'image-viewer.previous'
    };

    public viewerTranslations: ImageViewerTranslations = this.defaultTranslations;

    @Input()
    set imageSources(imgInput: string[]) {
        this.reset();
        this.hideFaceFrames();
        this._imageSources = imgInput;
    }

    @Input() actionsPosition: ViewerActionsPosition = ViewerActionsPosition.RIGHT_OVERLAY;

    @Input()
    index = 0;

    @Input()
    zoomFactor = 0.1;

    @Input()
    zoom = true;

    @Input()
    rotate = false;

    @Input() fullscreen: boolean = true;

    @Input()
    customActionItems: ActionItem[] = [];

    @Input() allowTransform: boolean = true;

    @Input() i18n: ImageViewerTranslationsPartial;

    faces = input([]);
    $faceFrames: WritableSignal<FaceFrame[]> = signal([]);

    @Output()
    indexChange: EventEmitter<number> = new EventEmitter();

    @ViewChild('image', { static: false }) image!: ElementRef;

    private readonly languageService: SharedLanguageService = inject(SharedLanguageService);
    private readonly faceInteractionService: FaceInteractionService = inject(FaceInteractionService);
    private readonly searchRoutingService: ShSearchRoutingService = inject(ShSearchRoutingService);

    constructor() {
        this.$focusedFaceId = this.faceInteractionService.$focusedFaceId;
        this.$hoveredFaceId = this.faceInteractionService.$hoveredFaceId;

        // Separating effects because combining them created infinite loops
        effect(() => {
            if (this.faces()) {
                this.updateFaceFrames();
            }
        });

        effect(() => {
            if (this.$focusedFaceId() || this.$hoveredFaceId()) {
                this.updateFaceFrames();
            }
        });
    }

    // #region Init
    ngOnInit() {
        this.initTranslations();
        this.initDragging();
        this.initImageButtons();
    }

    ngAfterViewInit() {
        this.initImageResizeObserver();
    }

    ngOnDestroy() {
        this.imageResizeObserver.unobserve(this.image.nativeElement);
    }

    private initTranslations(): void {
        this.viewerTranslations = {
            zoomIn: this.languageService.translate(this.defaultTranslations.zoomIn),
            zoomOut: this.languageService.translate(this.defaultTranslations.zoomOut),
            rotateClockwise: this.languageService.translate(this.defaultTranslations.rotateClockwise),
            rotateCounterClockwise: this.languageService.translate(this.defaultTranslations.rotateCounterClockwise),
            undo: this.languageService.translate(this.defaultTranslations.undo),
            next: this.languageService.translate(this.defaultTranslations.next),
            previous: this.languageService.translate(this.defaultTranslations.previous),
            ...this.i18n
        };
    }

    protected initDragging() {
        this.draggable = this.zoom;
    }

    private initImageButtons() {
        const actionItems: ActionItem[] = [];

        if (this.zoom) {
            actionItems.push(...this.getZoomActionItems());
        }

        if (this.rotate) {
            actionItems.push(...this.getRotateActionItems());
        }

        if (this.zoom || this.rotate) {
            const reset: ActionItem = {
                label: this.viewerTranslations.undo,
                icon: 'undo',
                eventListener: this.reset.bind(this, true)
            };

            actionItems.push(reset);
        }

        if (this.customActionItems.length > 0) {
            actionItems.push(...this.customActionItems);
        }

        this.imageActionButtons = actionItems.reverse();
    }

    private getZoomActionItems(): ActionItem[] {
        const zoomIn: ActionItem = {
            label: this.viewerTranslations.zoomIn,
            icon: 'zoom-in',
            eventListener: this.zoomIn.bind(this)
        };

        const zoomOut: ActionItem = {
            label: this.viewerTranslations.zoomOut,
            icon: 'zoom-out',
            eventListener: this.zoomOut.bind(this)
        };

        return [zoomIn, zoomOut];
    }

    private getRotateActionItems(): ActionItem[] {
        const rotateClockwise: ActionItem = {
            label: this.viewerTranslations.rotateClockwise,
            icon: 'rotate-clockwise',
            eventListener: this.rotateClockwise.bind(this)
        };

        const rotateCounterClockwise: ActionItem = {
            label: this.viewerTranslations.rotateCounterClockwise,
            icon: 'rotate-ct-clockwise',
            eventListener: this.rotateCounterClockwise.bind(this)
        };

        return [rotateClockwise, rotateCounterClockwise];
    }
    // #endregion

    // #region Load image
    onLoad(url: string): void {
        this.loading = false;
    }

    onLoadStart(url: string): void {
        this.loading = true;
    }

    imageNotFound(url: string): void {
        console.error('Image not found Url:', url);
    }
    // #endregion

    // #region Reset
    protected reset(transition: boolean = false): void {
        this.transition = transition;
        this.scale = 1;

        this.rotation = this.getClosestMultipleOf360(this.rotation);

        this.refocus();
        this.updateStyle();
    }

    private refocus(): void {
        this.translateX = 0;
        this.translateY = 0;
    }

    private getClosestMultipleOf360(angle: number): number {
        const horizontalAligned = angle + (angle % 180);
        const rotateTo = horizontalAligned - (horizontalAligned % 360);
        return rotateTo;
    }
    // #endregion

    // #region Zoom
    zoomIn() {
        this.hideFaceFrames();
        this.refocus();
        this.transition = true;
        this.scale *= 1 + this.zoomFactor;
        this.updateStyle();
    }

    zoomOut() {
        this.hideFaceFrames();
        this.refocus();
        this.transition = true;
        if (this.scale > this.zoomFactor) {
            this.scale /= 1 + this.zoomFactor;
        }
        this.updateStyle();
    }

    scrollZoom(evt: WheelEvent): void {
        if (this.zoom && (this.ctrlPressed$.value || evt.ctrlKey)) {
            if (evt.deltaY > 0) {
                this.zoomOut();
            } else {
                this.zoomIn();
            }
            evt.stopPropagation();
            evt.preventDefault();
        }
    }
    // #endregion

    // #region Rotate
    rotateClockwise(): void {
        this.hideFaceFrames();
        this.transition = true;
        this.rotation += 90;

        this.onRotate();
    }

    rotateCounterClockwise(): void {
        this.hideFaceFrames();
        this.transition = true;
        this.rotation -= 90;

        this.onRotate();
    }

    private onRotate(): void {
        if (!this.draggable || !this.zoom) {
            this.rescaleImgToFit();
        }
        this.updateStyle();
    }

    protected rescaleImgToFit(): void {
        const imgHeight: number = this.image.nativeElement.height;
        const imgWidth: number = this.image.nativeElement.width;
        const naturalHeight: number = this.image.nativeElement.naturalHeight;

        if (this.rotation % 180 === 0) {
            this.scale = 1;
        } else {
            const newScale: number = imgHeight / imgWidth;
            this.scale = imgHeight * newScale <= naturalHeight ? newScale : 1;
        }
    }
    // #endregion

    // #region Drag
    onDragOver(evt: DragEvent): void {
        if (!this.draggable || !this.dragging) {
            return;
        }
        evt.preventDefault();
        this.transition = false;
        this.translateX += evt.clientX - this.prevX;
        this.translateY += evt.clientY - this.prevY;
        this.prevX = evt.clientX;
        this.prevY = evt.clientY;
        this.updateStyle();
    }

    onDragStart(evt: DragEvent): void {
        this.hideFaceFrames();
        this.dragging = true;
        this.prevX = evt.clientX;
        this.prevY = evt.clientY;
    }

    onDragEnd(evt: any): void {
        this.dragging = false;
    }
    // #endregion

    // #region Navigate images
    @HostListener('mouseover')
    onMouseOver(): void {
        this.hovered = true;
    }

    @HostListener('mouseleave')
    onMouseLeave(): void {
        this.hovered = false;
    }

    @HostListener('window:keyup.ArrowRight', ['$event'])
    nextImage(event: Event) {
        if (this.canNavigate(event) && this.index < this._imageSources.length - 1) {
            this.loading = true;
            this.index++;
            this.triggerIndexBinding();
            this.reset();
        }
    }

    @HostListener('window:keyup.ArrowLeft', ['$event'])
    prevImage(event: Event) {
        if (this.canNavigate(event) && this.index > 0) {
            this.loading = true;
            this.index--;
            this.triggerIndexBinding();
            this.reset();
        }
    }

    private canNavigate(event: any): boolean {
        return event == null || this.hovered;
    }

    private triggerIndexBinding(): void {
        this.indexChange.emit(this.index);
    }
    // #endregion

    // #region Document events
    @HostListener('window:keydown.Ctrl', []) onKeyDown() {
        this.ctrlPressed$.next(true);
    }

    @HostListener('window:keyup.Ctrl', []) onKeyUp() {
        this.ctrlPressed$.next(false);
    }

    @HostListener('window:blur', ['$event'])
    handleBlur(event: FocusEvent) {
        this.ctrlPressed$.next(false);
    }

    // Handle when page becomes hidden (e.g., user switches tabs)
    @HostListener('document:visibilitychange', [])
    onVisibilityChange() {
        if (document.hidden) {
            this.ctrlPressed$.next(false);
        }
    }
    // #endregion

    // #region Faces
    public loadFaceFrames(reset: boolean = false) {
        if (
            !reset &&
            (this.rotation % 360 !== 0 || this.scale !== 1 || this.translateX !== 0 || this.translateY !== 0)
        ) {
            // Don't show faces when image is transformed
            return;
        }
        this.updateFaceFrames();
        this.showFaceFrames = true;
    }

    private updateFaceFrames(): void {
        const faces: Face[] = this.faces();
        if (faces.length === 0 || !this.image) {
            this.$faceFrames.set([]);
            return;
        }

        const imgElement = this.image.nativeElement;
        const imgWidth = imgElement.width;
        const imgHeight = imgElement.height;
        const naturalWidth = imgElement.naturalWidth;
        const naturalHeight = imgElement.naturalHeight;

        // Get the container element (img-container)
        const container = imgElement.parentElement;
        if (!container) {
            this.$faceFrames.set([]);
            return;
        }

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Calculate the offset due to centering (flexbox centers the image)
        const offsetX = (containerWidth - imgWidth) / 2;
        const offsetY = (containerHeight - imgHeight) / 2;

        // Calculate the scale factor from natural image size to displayed size
        const displayScaleX = imgWidth / naturalWidth;
        const displayScaleY = imgHeight / naturalHeight;

        const faceFrames = faces.map(face => {
            const bbox = face.BoundingBox;

            // Scale bounding box coordinates from natural image size to displayed size
            const scaledX = bbox.TopLeftX * displayScaleX;
            const scaledY = bbox.TopLeftY * displayScaleY;
            const width = bbox.Width * displayScaleX;
            const height = bbox.Height * displayScaleY;

            // Apply centering offset
            const x = scaledX + offsetX;
            const y = scaledY + offsetY;

            return {
                id: face.Id,
                x,
                y,
                width,
                height,
                name: face.Person?.Name,
                personId: face.Person?.RecordId
            };
        });

        this.$faceFrames.set(faceFrames);
    }

    private initImageResizeObserver(): void {
        this.imageResizeObserver = new ResizeObserver(entries => {
            entries.forEach(() => {
                this.updateFaceFrames();
            });
        });

        if (this.image) {
            this.imageResizeObserver.observe(this.image.nativeElement);
        }
    }

    public hideFaceFrames(): void {
        this.showFaceFrames = false;
    }

    public disableDrag(event: Event) {
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    public onFaceFrameClick(event: Event, frame: FaceFrame) {
        event.preventDefault();
        event.stopImmediatePropagation();

        this.faceInteractionService.scrollToName(frame.id);
    }
    // #endregion

    // #region Utils
    protected updateStyle(): void {
        this.style.transform = `translate(${this.translateX}px, ${this.translateY}px) rotate(${this.rotation}deg) scale(${this.scale})`;
        this.style.msTransform = this.style.transform;
        this.style.webkitTransform = this.style.transform;
        this.style.oTransform = this.style.transform;
    }

    public get faceUrlLink(): string[] {
        return this.searchRoutingService.getSearchRouterLink();
    }

    public getFaceParams(frame: FaceFrame): UrlQueryParams {
        return GenerateQueryParamsFromAdvancedSearchTerm(PERSON_ID_DEFINITION_DOTTED_KEY, [frame.personId!]);
    }

    // #endregion
}
